import Request from '../Request';
import { AnyRow, BenchmarkPayload, Capabilities, Cell, ClassReportPayload, Freshness, ProductRowsPayload, ProductSearchResult, ReportKey, ReportParams, ReportPayloadBase, SkuRowsPayload, Summary, VendorRowsParams, VendorRowsPayload, isVendorRow } from '../../interfaces/reports/WeeklyPlanning.interface';

export type ApiErrorKind = 'validation' | 'unsupported' | 'degraded' | 'unauthorized' | 'failed';

export class WeeklyPlanningApiError extends Error {
  constructor(public kind: ApiErrorKind, public status: number | null, message: string, public payload?: unknown) {
    super(message);
    this.name = 'WeeklyPlanningApiError';
    // TS targets ES5: restore the prototype so instanceof works on the subclass.
    Object.setPrototypeOf(this, WeeklyPlanningApiError.prototype);
  }
}

const REPORT_PATH: Record<ReportKey, string> = { inventory_health: 'inventory-health', plan_otb: 'plan-otb', weekly_action: 'weekly-action' };
const base = (clientId: number | string) => `/api/clients/${clientId}/weekly-planning-reports`;

// Laravel's validation body is {message: {field: [msg, ...]}}; jsonError's is {message: 'text'}.
const messageOf = (data: any, fallback: string): string => {
  const m = data?.message;
  if (typeof m === 'string') return m;
  if (m && typeof m === 'object') {
    const first = Object.values(m)[0];
    if (Array.isArray(first) && typeof first[0] === 'string') return first[0];
  }
  return fallback;
};

const toError = (e: any): WeeklyPlanningApiError => {
  const status: number | undefined = e?.response?.status;
  const data = e?.response?.data;
  if (status === 400) return new WeeklyPlanningApiError('validation', 400, messageOf(data, 'Invalid report parameters.'), data);
  if (status === 403) return new WeeklyPlanningApiError('unsupported', 403, messageOf(data, 'This report is not available for this client.'), data);
  if (status === 503) return new WeeklyPlanningApiError('degraded', 503, messageOf(data, "Could not determine the client's data coverage."), data);
  if (status === 401) return new WeeklyPlanningApiError('unauthorized', 401, 'Your session has expired.', data);
  return new WeeklyPlanningApiError('failed', status ?? null, e?.message || 'The report could not be loaded.', data);
};

async function request<T>(uri: string, params?: Record<string, unknown>): Promise<T> {
  try {
    const res = await Request.get(uri, params, true);
    return res.data as T;
  } catch (e) {
    throw toError(e);
  }
}

// Compact cells on the wire: the controller omits every null / false key of the cells, summary and
// freshness, rounds amounts (2 dp) and shares (4 dp), and marks the payload `cellsCompact: true` (CellCompactor.php). These lists are the full cell contract (RowHelpers::emptyRow /
// ReportAssembler::emptyCell): every key is restored here with its default, so the kit and the reports never see a
// compact cell. Class rows never carry the three vendor / SKU-only keys, so they do not gain them (class
// cells carry inventoryCostStart / End and inflowCostPlan, so those come back on every row). Keys outside the
// lists (trendNote on lastYear cells, trendAlt) are kept exactly as sent.
const CELL_NULL_KEYS: (keyof Cell)[] = ['sales', 'unitsSold', 'markdowns', 'inflow', 'inflowCost', 'salesPlan', 'inflowPlan', 'inflowCostPlan', 'inventoryPlan', 'markdownsPlan',
  'unitsStart', 'unitsEnd', 'inventoryStart', 'inventoryEnd', 'inventoryCostStart', 'inventoryCostEnd', 'salesLy', 'markdownsLy', 'inflowLy', 'inventoryLy',
  'trendPct', 'salesTrend', 'trendBasis'];
const CELL_FALSE_KEYS: (keyof Cell)[] = ['isEstimated', 'isCurrent', 'isAllocated', 'planRolledUp', 'isProjected', 'isFloored', 'lyPartial', 'inventoryLyMissing', 'planV2History'];
const VENDOR_ONLY_KEYS: (keyof Cell)[] = ['unitsStart', 'unitsEnd', 'isFloored'];
const buildDefaults = (vendorRow: boolean): Cell => {
  const d: Record<string, unknown> = {};
  CELL_NULL_KEYS.forEach((k) => { if (vendorRow || !VENDOR_ONLY_KEYS.includes(k)) d[k] = null; });
  CELL_FALSE_KEYS.forEach((k) => { if (vendorRow || !VENDOR_ONLY_KEYS.includes(k)) d[k] = false; });
  return d as unknown as Cell;
};
const VENDOR_CELL_DEFAULTS = buildDefaults(true);
const CLASS_CELL_DEFAULTS = buildDefaults(false);

/** A full cell from a compact one (a PHP empty array arrives as []: treated as an empty cell). */
export const expandCell = (cell: unknown, vendorRow: boolean): Cell => {
  const present = cell && typeof cell === 'object' && !Array.isArray(cell) ? (cell as Partial<Cell>) : {};
  return { ...(vendorRow ? VENDOR_CELL_DEFAULTS : CLASS_CELL_DEFAULTS), ...present };
};

// The summary and freshness blocks are compacted the same way (RowHelpers::summary / ReportAssembler::classSummary and
// FreshnessService): every key the types declare comes back. targetSource is on vendor-level rows only; the two unit
// fields and reorderBasis (the compactor strips a null basis) on product and SKU rows only (SkuRowsAssembler /
// ProductRowsAssembler); a null block stays null.
const SUMMARY_NULL_KEYS: (keyof Summary)[] = ['inventory', 'avgPeriodSales', 'wos', 'targetWos', 'targetBasis', 'status', 'onOrder', 'onOrderCost', 'receivedToDate', 'inflowPlanSpan', 'otb', 'otbBasis', 'otbCoveredThrough', 'spanBomPlan', 'spanBomActual', 'deviation', 'otbDev', 'otbExcluded', 'planBasis', 'otbOnOrder', 'serviceTypeId', 'action',
  'inflowCostPlanSpan', 'receivedCostToDate', 'otbOnOrderCost', 'otbCost', 'otbByPeriod'];
const SUMMARY_FALSE_KEYS: (keyof Summary)[] = ['inventoryKnown', 'urgent', 'deadstock']; // deadstock: the compactor strips a false flag
const FRESHNESS_KEYS: (keyof Freshness)[] = ['onHandUnits', 'onHandRetail', 'freshUnits', 'freshRetail', 'aging4wUnits', 'aging4wRetail', 'aging8wUnits', 'aging8wRetail', 'aging12wUnits', 'aging12wRetail', 'freshPctSku', 'aging4wPct', 'aging8wPct', 'aging12wPct'];
const asBlock = (v: unknown): Record<string, unknown> | null => (v && typeof v === 'object' ? (v as Record<string, unknown>) : null);

export const expandSummary = (summary: unknown, row: AnyRow): Summary | null => {
  const present = asBlock(summary);
  if (present === null) return null;
  const d: Record<string, unknown> = {};
  SUMMARY_NULL_KEYS.forEach((k) => { d[k] = null; });
  SUMMARY_FALSE_KEYS.forEach((k) => { d[k] = false; });
  if (isVendorRow(row)) {
    d.targetSource = null;
    if (row.level === 'sku' || row.level === 'product') { d.avgPeriodUnits = null; d.suggestedReorderUnits = null; d.reorderBasis = null; }
  }
  return { ...d, ...present } as unknown as Summary;
};

export const expandFreshness = (freshness: unknown): Freshness | null => {
  const present = asBlock(freshness);
  if (present === null) return null;
  const d: Record<string, unknown> = {};
  FRESHNESS_KEYS.forEach((k) => { d[k] = null; });
  return { ...d, ...present } as unknown as Freshness;
};

/** The payload with full cells, summary and freshness on every row (a new object; the input is untouched). A payload without the flag is returned as is. */
export function expandCompactCells<T extends ReportPayloadBase<AnyRow>>(payload: T): T {
  if (!payload || !(payload as { cellsCompact?: boolean }).cellsCompact) return payload;
  const rest: T & { cellsCompact?: boolean } = { ...payload };
  delete rest.cellsCompact;
  const rows = (payload.rows ?? []).map((row) => {
    if (!row || !('periods' in row)) return row;
    const vendorRow = isVendorRow(row);
    const periods: Record<string, Cell> = {};
    const src = row.periods && typeof row.periods === 'object' ? row.periods : {};
    Object.keys(src).forEach((p) => { periods[p] = expandCell((src as Record<string, unknown>)[p], vendorRow); });
    const out: AnyRow = { ...row, periods, summary: expandSummary(row.summary, row) };
    if ('freshness' in row) (out as { freshness: Freshness | null }).freshness = expandFreshness((row as { freshness: unknown }).freshness);
    return out;
  });
  return { ...rest, rows } as unknown as T;
}

const rows = <T extends ReportPayloadBase<AnyRow>>(uri: string, params?: Record<string, unknown>): Promise<T> =>
  request<T>(uri, params).then(expandCompactCells);

/**
 * A batch answer (/sku-rows or /product-rows with `parents`, Performance: one request for a chain's member stores): the
 * payload each parent would have returned on its own, with the fields every parent shares sent once in `common`
 * (CellCompactor::compactBatch). A parent absent from `byParent` is absent from the result (the caller treats it as failed).
 */
export interface BatchAnswer { batch: true; cellsCompact?: boolean; common: Record<string, unknown>; byParent: Record<string, Record<string, unknown>> }

/** parent -> its payload, with full cells: {...common, ...byParent[parent]} through expandCompactCells. */
export function expandBatch<T extends ReportPayloadBase<AnyRow>>(answer: BatchAnswer): Record<string, T> {
  const out: Record<string, T> = {};
  Object.keys(answer?.byParent ?? {}).forEach((parent) => {
    out[parent] = expandCompactCells({ ...answer.common, ...answer.byParent[parent], cellsCompact: answer.cellsCompact } as unknown as T);
  });
  return out;
}

const batchRows = <T extends ReportPayloadBase<AnyRow>>(uri: string, parents: string[], params: ReportParams): Promise<Record<string, T>> =>
  request<BatchAnswer>(uri, { ...params, parents: JSON.stringify(parents) }).then((a) => expandBatch<T>(a));

const WeeklyPlanningApi = {
  getCapabilities: (clientId: number | string) => request<Capabilities>(`${base(clientId)}/capabilities`),
  getReport: (report: ReportKey, clientId: number | string, params: ReportParams) =>
    rows<ClassReportPayload>(`${base(clientId)}/${REPORT_PATH[report]}`, { ...params }),
  getVendorRows: (clientId: number | string, params: VendorRowsParams) =>
    rows<VendorRowsPayload>(`${base(clientId)}/vendor-rows`, { ...params }),
  getSkuRows: (clientId: number | string, parent: string, params: ReportParams) =>
    rows<SkuRowsPayload>(`${base(clientId)}/sku-rows`, { ...params, parent }),
  // One request for several parents (a chain's member stores): class:<id> parents (and vendor:<key>:class:<id> for SKU rows), at most 25.
  getSkuRowsBatch: (clientId: number | string, parents: string[], params: ReportParams) =>
    batchRows<SkuRowsPayload>(`${base(clientId)}/sku-rows`, parents, params),
  getProductRowsBatch: (clientId: number | string, parents: string[], params: ReportParams) =>
    batchRows<ProductRowsPayload>(`${base(clientId)}/product-rows`, parents, params),
  // parent: the canonical scope string, sent as is (the caller builds classes:<sorted, de-duplicated ids>).
  getProductRows: (clientId: number | string, parent: string, params: ReportParams) =>
    rows<ProductRowsPayload>(`${base(clientId)}/product-rows`, { ...params, parent }),
  // W3: server-side product search (title, SKU, barcode, tags, whatever the client's POS has). `q` is 2 to 100 characters;
  // the report params only set the sales window that ranks the matches. A plain request: the payload has no row cells.
  searchProducts: (clientId: number | string, q: string, params: ReportParams) =>
    request<ProductSearchResult>(`${base(clientId)}/product-search`, { ...params, q }),
  // month = Monthly's target epoch month; null in Weekly (the server picks the latest complete month).
  // Plain request, not rows: the benchmark payload has no row cells and the controller does not compact it.
  getBenchmarks: (clientId: number | string, month: number | null) =>
    request<BenchmarkPayload>(`${base(clientId)}/benchmarks`, month === null ? {} : { month }),
};
export default WeeklyPlanningApi;
